/**
 * Activity timeline on the invoice detail page (design D): newest first,
 * 22 px glyph squares joined by a connector line; sent, delivered, read,
 * failed and shared events use the channel's colour and letter. Copy comes
 * from detailModel.eventView so it is tested without rendering.
 */
import React from 'react';
import type { InvoiceEvent } from '../../../internals/repository/InvoiceRepository';
import { ago, dateTime } from '../format';
import { eventView, GlyphTone } from '../detailModel';

const TONE: Record<GlyphTone, { bg: string; fg: string }> = {
  neutral: { bg: 'var(--neutralSoft)', fg: 'var(--text2)' },
  brand: { bg: 'var(--brandSoft)', fg: 'var(--brand)' },
  pos: { bg: 'var(--posSoft)', fg: 'var(--posFg)' },
  warn: { bg: 'var(--warnSoft)', fg: 'var(--warnFg)' },
  neg: { bg: 'var(--negSoft)', fg: 'var(--negFg)' },
  info: { bg: 'var(--infoSoft)', fg: 'var(--infoFg)' },
};

export default function ActivityTimeline({ events, currency }: { events: InvoiceEvent[]; currency?: string }) {
  const list = [...events].sort((a, b) => b.createdAt - a.createdAt);
  return (
    <section className="nl-card flex flex-col p-5" aria-labelledby="inv-activity">
      <h2 id="inv-activity" className="mb-3 text-[14px] font-semibold text-text1">Activity</h2>
      {list.length === 0 && <p className="text-[13px] text-text3">Nothing has happened yet.</p>}
      <ol className="m-0 list-none p-0">
        {list.map((e, i) => {
          const v = eventView(e, currency);
          const t = TONE[v.tone];
          return (
            <li key={e.eventId} className="grid gap-2.5" style={{ gridTemplateColumns: '26px minmax(0,1fr)' }}>
              <div className="flex flex-col items-center">
                {v.channel ? (
                  <span
                    aria-hidden
                    className="grid shrink-0 place-items-center rounded-md text-[11px] font-bold text-white"
                    style={{ width: 22, height: 22, background: v.channel.bg }}
                  >
                    {v.channel.letter}
                  </span>
                ) : (
                  <span
                    aria-hidden
                    className="grid shrink-0 place-items-center rounded-md text-[10px] font-bold"
                    style={{ width: 22, height: 22, background: t.bg, color: t.fg }}
                  >
                    {v.glyph}
                  </span>
                )}
                {i < list.length - 1 && <span className="w-px flex-1 bg-borderc" style={{ minHeight: 12 }} />}
              </div>
              <div className="flex min-w-0 flex-col pb-3">
                <span
                  className={`break-words text-[13px] font-medium ${e.type === 'FAILED' ? '' : 'text-text1'}`}
                  style={e.type === 'FAILED' ? { color: 'var(--negFg)' } : undefined}
                >
                  {v.text}
                </span>
                <span className="text-[12px] text-text3" title={ago(e.createdAt)}>{dateTime(e.createdAt)}</span>
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
}
